// src/api/apiClient.js
// Cliente HTTP con JWT Bearer + refresh automatico + redireccion a /login en 401 definitivo.
// Sprint 15.

// Sprint 54 - Parametrizado via .env.staging / .env.production (CRA, REACT_APP_*)
const target = process.env.REACT_APP_TARGET_ENV || 'staging';
export const API_BASE =
  target === 'prod'
    ? process.env.REACT_APP_API_URL_PROD
    : process.env.REACT_APP_API_URL_STAGING;

const STORAGE_ACCESS = 'access_token';
const STORAGE_REFRESH = 'refresh_token';
const STORAGE_USER = 'user';
const STORAGE_COMPANY = 'stockpilot_company_id';

function getCurrentCompanyId() {
  try {
    const v = localStorage.getItem(STORAGE_COMPANY);
    return v ? v : null;
  } catch { return null; }
}


function getAccessToken() {
  return localStorage.getItem(STORAGE_ACCESS);
}

function getRefreshToken() {
  return localStorage.getItem(STORAGE_REFRESH);
}

function setAccessToken(token) {
  localStorage.setItem(STORAGE_ACCESS, token);
}

function setRefreshToken(token) {
  localStorage.setItem(STORAGE_REFRESH, token);
}

function setUser(user) {
  if (user) {
    localStorage.setItem(STORAGE_USER, JSON.stringify(user));
  } else {
    localStorage.removeItem(STORAGE_USER);
  }
}

function getUser() {
  const raw = localStorage.getItem(STORAGE_USER);
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function clearAuth() {
  localStorage.removeItem(STORAGE_ACCESS);
  localStorage.removeItem(STORAGE_REFRESH);
  localStorage.removeItem(STORAGE_USER);
}

async function refreshAccessToken() {
  const refresh = getRefreshToken();
  if (!refresh) return null;

  try {
    const res = await fetch(`${API_BASE}/auth/refresh`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${refresh}`,
        'Content-Type': 'application/json',
      },
    });
    if (!res.ok) return null;
    const data = await res.json();
    if (!data?.access_token) return null;
    setAccessToken(data.access_token);
    return data.access_token;
  } catch {
    return null;
  }
}

function redirectToLogin() {
  if (typeof window === 'undefined') return;
  if (window.location.pathname !== '/login') {
    window.location.href = '/login';
  }
}

async function request(method, path, body = null, retry = true) {
  const token = getAccessToken();
  const headers = { 'Content-Type': 'application/json' };
  if (token) headers.Authorization = `Bearer ${token}`;
  // Sprint 33 - Multi-tenant: enviar company_id como header (el backend lo valida contra JWT)
  const companyId = getCurrentCompanyId();
  if (companyId) headers['X-Company-Id'] = companyId;

  const init = { method, headers };
  if (body !== null && body !== undefined) {
    init.body = JSON.stringify(body);
  }

  let res;
  try {
    res = await fetch(`${API_BASE}${path}`, init);
  } catch (networkErr) {
    const e = new Error('No se pudo conectar con el servidor');
    e.cause = networkErr;
    throw e;
  }

  // 401 con posibilidad de refresh
  if (res.status === 401 && retry) {
    const newToken = await refreshAccessToken();
    if (newToken) {
      return request(method, path, body, false);
    }
    clearAuth();
    redirectToLogin();
    const err = new Error('Sesion expirada');
    err.status = 401;
    throw err;
  }

  // Intentamos parsear JSON; si no se puede, lo dejamos como null
  let payload = null;
  try {
    payload = await res.json();
  } catch {
    payload = null;
  }

  if (!res.ok) {
    const err = new Error(payload?.message || `HTTP ${res.status}`);
    err.status = res.status;
    err.data = payload;
    throw err;
  }

  return payload;
}

const apiClient = {
  API_BASE,
  get: (path) => request('GET', path),
  post: (path, body) => request('POST', path, body),
  put: (path, body) => request('PUT', path, body),
  delete: (path) => request('DELETE', path),
  // Helpers de storage (expuestos para AuthContext)
  getAccessToken,
  getRefreshToken,
  setAccessToken,
  setRefreshToken,
  getUser,
  setUser,
  clearAuth,
  getCurrentCompanyId,
};

export default apiClient;
